<template>
  <div :class="['scratchcard-slide-show', { loading: !slideDimensions.initialized }]">
    <LcCarousel
      v-if="slideDimensions.initialized"
      v-bind="{ slides }"
      aria-roledescription="carousel"
      :class="[
        'carousel',
        {
          'in-progress': isInProgress(state),
          'images': isScanPresent(state),
        },
      ]"
      :disable-swipe="slides.length <= 1"
      key-prop="serialNumber"
      :move-triggers="['click', 'keyboard']"
      role="region"
      variant="highlight"
    >
      <template #slide="{ attributes, index, isHighlighted: focused, slide }">
        <article
          v-bind="attributes"
          :aria-label="
            $t('common.carousel.slide.arialabel', {
              slide: index + 1,
              totalSlides: slides.length,
            })
          "
          :class="[
            'slide',
            {
              focused: focused,
              unrevealed: isSlideDetail(slide) && isRevealing(slide.status) && renderGlowEffect,
            },
          ]"
          :inert="focused ? undefined : 'true'"
        >
          <LazyScratchcardSlideInProgress
            v-if="isSlideDetail(slide) && isInProgress(slide.status)"
            v-bind="{ focused }"
            :serial-number="slide.serialNumber"
            @click:inprogress="emit('click:inprogress', slide.serialNumber)"
          />

          <LazyScratchcardSlideSummaryLoose
            v-else-if="isSummaryLooseCard(slide)"
            v-bind="{ focused }"
          />

          <LazyScratchcardSlideSummaryWin
            v-else-if="isSummaryWinCard(slide)"
            v-bind="{ focused }"
            :winnings="slide.winnings"
          />

          <LazyScratchcardSlideTicket
            v-else-if="slide.images"
            v-bind="{ mode, focused }"
            :images="slide.images"
            :order-status="state"
            :status="slide.status"
            :winnings="slide.winningsPayload"
            @click:details="emit('click:details', slide.serialNumber)"
            @click:ticket="emit('click:ticket', slide.serialNumber)"
            @click:zoom="emit('click:zoom', slide.serialNumber)"
          />
        </article>
      </template>
    </LcCarousel>
  </div>
</template>

<script lang="ts" setup>
import type {
  ScratchcardDetail,
  ScratchcardState,
  WinningsPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { SCRATCHCARD_DISPLAY_MODES, type ScratchcardDisplayMode } from './types'

type ScratchcardSlideShowEmits = {
  (
    e: 'click:details' | 'click:inprogress' | 'click:ticket' | 'click:zoom',
    serialNumber: string,
  ): void
}

type ScratchcardSlideShowProps = {
  mode?: ScratchcardDisplayMode
  state: ScratchcardState
  tickets: Omit<ScratchcardDetail, '__typename'>[]
  winnings: Winnings
}

type Slide = ScratchcardDetail | SummaryCardLoose | SummaryCardWin

type SummaryCardLoose = {
  serialNumber: string
  type: 'loose'
}

type SummaryCardWin = {
  serialNumber: string
  type: 'win'
  winnings: NonNullable<Winnings>
}

type Winnings = Maybe<Pick<WinningsPayload, 'amount' | 'freeTickets'>>

const emit = defineEmits<ScratchcardSlideShowEmits>()
const {
  mode = SCRATCHCARD_DISPLAY_MODES.FRONT_UNSCRATCHED,
  state,
  tickets,
  winnings,
} = defineProps<ScratchcardSlideShowProps>()

const { renderGlowEffect } = useScratchcardScratchSettings()

const prefersReducedMotion = useReducedMotion()

const slideDimensions = reactive({
  initialized: false,
  margin: 0,
  width: 0,
})

const slides = computed<Slide[]>(() => {
  if (!isRevealed(state)) return tickets

  const imageProperties = tickets[0].images?.scratchedImageProperties
  if (!imageProperties) return tickets

  let summaryCard: SummaryCardLoose | SummaryCardWin = {
    serialNumber: 'summary',
    type: 'loose',
  }

  if (winnings && ((winnings.amount?.value ?? 0) > 0 || winnings.freeTickets > 0)) {
    summaryCard = { ...summaryCard, type: 'win', winnings }
  }

  return [summaryCard, ...tickets]
})

const isSlideDetail = (slide: Slide): slide is ScratchcardDetail =>
  (slide as ScratchcardDetail).serialNumber !== undefined

const isSummaryLooseCard = (slide: Slide): slide is SummaryCardLoose =>
  (slide as SummaryCardLoose).type === 'loose'

const isSummaryWinCard = (slide: Slide): slide is SummaryCardWin =>
  (slide as SummaryCardWin).type === 'win'

const setCarouselDimensions = async () => {
  const firstTicket = tickets[0].images?.scratchedImageProperties
  if (!firstTicket) return

  const { height: ticketHeight, width: ticketWidth } = firstTicket

  const slideHeight = Math.min(ticketHeight, 400)
  const widthAspectRatio = ticketWidth / ticketHeight
  const slideWidth = Math.max(slideHeight * widthAspectRatio, 160)

  const heightAspectRatio = ticketHeight / ticketWidth
  const baseHeight = slideWidth * heightAspectRatio
  const scaledTicketHeight = baseHeight * 1.08

  slideDimensions.width = slideWidth
  slideDimensions.margin = (scaledTicketHeight - baseHeight) / 2

  if (slideDimensions.initialized) return

  if (!prefersReducedMotion.value) await waitMs(50)
  await nextTick()
  slideDimensions.initialized = true
}

watchImmediate(slides, setCarouselDimensions)

watchOnce(
  () => state,
  (newValue) => {
    if (!isInProgress(newValue)) return
    slideDimensions.initialized = true
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@use './scratchcard' as *;

.scratchcard-slide-show {
  .carousel {
    &.in-progress {
      .slide {
        margin: 3.5rem token('size/space/gap/100');
      }
    }

    &.images {
      .slide {
        min-width: calc(v-bind('slideDimensions.width') * 1px);
        max-width: calc(v-bind('slideDimensions.width') * 1px);
        margin: calc((v-bind('slideDimensions.margin') * 1px) + 2.25rem) token('size/space/gap/100');
      }
    }
  }

  .slide {
    opacity: 0.6;

    &.focused {
      opacity: 1;

      &.unrevealed {
        &::before,
        &::after {
          content: '';
          position: absolute;
        }

        &::before {
          inset: -1px;

          @include glow;
        }

        &::after {
          inset: 0;
          background: linear-gradient(
            to right,
            rgb(238, 249, 255, 0%) 0%,
            rgb(238, 249, 255, 60%) 50%,
            rgb(238, 249, 255, 0%) 100%
          );
          background-size: 200% 100%;
          background-repeat: no-repeat;
          pointer-events: none;
          will-change: background-position;
          animation: move-hint 6s ease infinite;
        }
      }
    }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-carousel__container) {
    @include media-desktop {
      position: relative;
      overflow: hidden;

      &::after,
      &::before {
        content: '';
        position: absolute;
        top: 0;
        z-index: 10;
        width: 4rem;
        height: 100%;
      }

      &::after {
        right: 0;
        background: linear-gradient(
          90deg,
          rgb(236, 241, 255, 0%) 4.54%,
          token('color/background/secondary/secondary/bold') 90.63%
        );
      }

      &::before {
        left: 0;
        background: linear-gradient(
          270deg,
          rgb(236, 241, 255, 0%) 4.54%,
          token('color/background/secondary/secondary/bold') 90.63%
        );
      }
    }
  }

  &.loading {
    min-height: 31.25rem;
  }

  @keyframes move-hint {
    0% {
      background-position: 200% 0%;
    }

    100% {
      background-position: -150% 0%;
    }
  }
}
</style>
